import React from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Email-gated Reward Wheel.
 *
 * Flow:
 *   1. After 12s on the page, a small teaser pill flies in.
 *   2. Click teaser → modal opens with email input ("Drop your email to spin").
 *   3. User submits email → backend records spin, returns the winning prize.
 *   4. Wheel animates to land on that prize.
 *   5. "Claim & Browse" → directs to /menu#breakdown.
 *
 * Backend rules:
 *   - One spin per email per campaign id (admin can roll the campaign to reset all).
 *   - Email is normalized lowercase/trimmed before storage.
 *   - All emails captured in /api/admin/wheel/spins for marketing campaigns.
 */
const SEGMENTS = [
  { id: "5off",    label: "5% Off",          sub: "Discount unlocked",      deg:   0 },
  { id: "preroll", label: "Free Pre-roll",   sub: "Bonus on next order",    deg:  60 },
  { id: "sticker", label: "Sticker Pack",    sub: "Members merch unlocked", deg: 120 },
  { id: "jar",     label: "Free Jar Upgrade",sub: "Signature jar included", deg: 180 },
  { id: "mystery", label: "Mystery Bonus",   sub: "Surprise on delivery",   deg: 240 },
];

const SEEN_KEY_BASE = "2high_rw_seen_v2";
const EMAIL_KEY = "2high_rw_email";
const DELAY_MS = 12000; // 12 seconds before teaser appears

export default function RewardWheel({ enabled = true, ageOk, content = {}, prizes }) {
  const [stage, setStage] = React.useState("idle"); // idle | teaser | popup
  const [angle, setAngle] = React.useState(0);
  const [spinning, setSpinning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [email, setEmail] = React.useState("");
  const [err, setErr] = React.useState("");
  const [alreadySpun, setAlreadySpun] = React.useState(false);

  const wheelEnabled = content.reward_wheel_enabled !== false;
  const campaignId = content.reward_wheel_campaign_id || "v1";
  const SEEN_KEY = `${SEEN_KEY_BASE}_${campaignId}`;
  // Operator-editable prize list (falls back to the static SEGMENTS if not provided).
  const prizeList = React.useMemo(() => {
    const src = Array.isArray(prizes) && prizes.length ? prizes : SEGMENTS;
    const n = src.length;
    return src.map((p, i) => ({
      id: p.id || `prize${i}`,
      label: p.label || `Prize ${i + 1}`,
      sub: p.sub || "",
      deg: typeof p.deg === "number" ? p.deg : Math.round(i * (360 / n)),
    }));
  }, [prizes]);
  const segCount = prizeList.length || 1;
  const segSize = 360 / segCount;
  // Build a dynamic conic-gradient that always has the right number of slices.
  const wheelBg = React.useMemo(() => {
    const palette = ["#c9b88a", "#1e3a5f", "#fff3d2", "#1e3a5f", "#c9b88a", "#2a1d3d", "#5a1a3a", "#3a2e15"];
    const stops = prizeList.map((_, i) => {
      const c = palette[i % palette.length];
      return `${c} ${i * segSize}deg ${(i + 1) * segSize}deg`;
    });
    return `conic-gradient(${stops.join(",")})`;
  }, [prizeList, segSize]);

  // Surface the teaser 12s after age gate is passed (once per browser per campaign).
  React.useEffect(() => {
    if (!enabled || !ageOk || !wheelEnabled) return;
    let seen = false;
    try { seen = !!localStorage.getItem(SEEN_KEY); } catch {}
    if (seen) return;
    const t = setTimeout(() => setStage("teaser"), DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled, ageOk, wheelEnabled, SEEN_KEY]);

  function dismissTeaser() {
    setStage("idle");
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
  }

  function openPopup() {
    setStage("popup");
    setErr("");
    // Pre-fill email if they spun this campaign before
    try {
      const saved = localStorage.getItem(EMAIL_KEY) || "";
      if (saved) setEmail(saved);
    } catch {}
  }

  function closePopup() {
    setStage("idle");
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
  }

  async function submitAndSpin(e) {
    if (e) e.preventDefault();
    setErr("");
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cleaned)) {
      setErr("Please enter a valid email address.");
      return;
    }
    if (spinning) return;
    // Mark seen IMMEDIATELY so the popup never re-appears even if the user
    // closes mid-spin or claims before the animation finishes.
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
    setSpinning(true);
    try {
      const res = await axios.post(`${BACKEND}/api/public/wheel/spin`, {
        email: cleaned,
        source: "wheel_popup",
      });
      const data = res.data || {};
      try { localStorage.setItem(EMAIL_KEY, cleaned); } catch {}

      if (data.already_spun) {
        setAlreadySpun(true);
        // Just reveal their previous prize instantly (no animation)
        setResult({
          id: data.prize_id,
          label: data.prize_label,
          sub: data.prize_sub,
          deg: data.prize_deg,
        });
        setAngle((360 * 4) + (360 - (data.prize_deg + (segSize / 2))));
        setSpinning(false);
        return;
      }

      // Animate the wheel landing on the won prize
      const turns = 6;
      const segCenter = data.prize_deg + (segSize / 2);
      const final = (turns * 360) + (360 - segCenter);
      setAngle(final);
      setTimeout(() => {
        setResult({
          id: data.prize_id,
          label: data.prize_label,
          sub: data.prize_sub,
          deg: data.prize_deg,
        });
        setSpinning(false);
        try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
      }, 4600);
    } catch (e) {
      setSpinning(false);
      const msg = e.response?.data?.detail || "Something went wrong. Try again.";
      setErr(msg);
    }
  }

  function claim() {
    setStage("idle");
    setTimeout(() => { window.location.href = "/menu#breakdown"; }, 300);
  }

  if (stage === "idle" || !ageOk || !wheelEnabled) return null;

  return (
    <>
      {stage === "teaser" && (
        <div
          className="rt-teaser show"
          data-testid="reward-teaser"
          onClick={(e) => {
            if (e.target.closest("[data-rt-close]")) { dismissTeaser(); return; }
            openPopup();
          }}
        >
          <div className="rt-icon">
            <svg viewBox="0 0 24 24"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4z"/></svg>
          </div>
          <div className="rt-text">
            <b>Spin for a member reward</b>
            <small>Drop your email · One spin</small>
          </div>
          <span className="rt-x" data-rt-close data-testid="reward-teaser-close">✕</span>
        </div>
      )}

      {stage === "popup" && (
        <div
          className="popup show"
          data-testid="reward-popup"
          onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}
        >
          <div className="popup-card" style={{ maxWidth: 540 }}>
            <button className="x" data-testid="reward-close-btn" onClick={closePopup} aria-label="Close">✕</button>
            <div className="ml">— BONUS MISSION · MEMBERS ONLY —</div>
            <h3>One email, <em>one spin.</em></h3>
            <p>Drop your email to unlock the wheel. We'll send you the occasional drop alert — never spam.</p>

            <div className="wheel-wrap">
              <div className="wheel-pointer" />
              <div
                className="wheel"
                style={{
                  background: wheelBg,
                  transform: `rotate(${angle}deg)`,
                  transition: spinning ? "transform 4.4s cubic-bezier(.18,.9,.22,1)" : "transform .3s",
                }}
              />
              <div className="wheel-hub">2H</div>
            </div>

            {/* Email form OR result */}
            {!result ? (
              <form onSubmit={submitAndSpin} noValidate style={{ width: "100%", maxWidth: 380, margin: "8px auto 0" }} data-testid="reward-email-form">
                <label style={{ display: "block", fontSize: 10, letterSpacing: ".22em", textTransform: "uppercase", color: "#8e8f7f", marginBottom: 6, textAlign: "center" }}>
                  Your email
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (err) setErr(""); }}
                  placeholder="you@example.com"
                  data-testid="reward-email-input"
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    background: "rgba(8,10,8,.6)",
                    border: `1px solid ${err ? "#c25" : "rgba(201,168,107,.35)"}`,
                    borderRadius: 10,
                    color: "#e8e4d8",
                    fontSize: 14,
                    textAlign: "center",
                    fontFamily: "inherit",
                  }}
                />
                {err && (
                  <div style={{ color: "#e88", fontSize: 11, marginTop: 6, textAlign: "center" }} data-testid="reward-error">
                    {err}
                  </div>
                )}
                <div style={{ marginTop: 14, display: "flex", justifyContent: "center" }}>
                  <button
                    type="submit"
                    className="btn solid"
                    data-testid="reward-spin-btn"
                    disabled={spinning}
                    style={{ opacity: spinning ? 0.55 : 1 }}
                  >
                    {spinning ? "Spinning…" : "Spin the Wheel"}
                  </button>
                </div>
                <div style={{ fontSize: 10, color: "#6e6f5f", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
                  21+ members only · One spin per email · We'll never share your address
                </div>
              </form>
            ) : (
              <>
                <div className="wheel-result" data-testid="reward-result-label">{result.label}</div>
                <div className="wheel-result-sub" data-testid="reward-result-sub">{result.sub}</div>
                {alreadySpun && (
                  <div style={{ textAlign: "center", fontSize: 11, color: "#c9a86b", marginTop: 6 }}>
                    Welcome back · This was your previous prize
                  </div>
                )}
                <div className="cta-row" style={{ justifyContent: "center", marginTop: 12 }}>
                  <button className="btn solid" data-testid="reward-claim-btn" onClick={claim}>
                    Claim & Browse →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
